import { cx } from '@emotion/css';
import { useMemo, useState } from 'react';
import { useGetFeaturedLinks } from './useGetFeaturedLinks';
import getValidHref from '@/utils/getValidHref';
import styles from './InteractiveFlyer.styles';
import { CallToAction, CallToActionEnum } from '@/graphql/types';
import { TrackingFrom, useAnalytics, useMetrics } from '@/hooks';
import { getCTAMetric } from '@/utils/metrictCTAType';
import getFeatureLinkTracking from '@/utils/featureLinksTracking';

type Props = {
  districtId: number;
  flyerId: number;
  pageNumber: number;
  flyerImage: string;
  highlightedLink?: CallToAction['id'];
  highlightLink: (id: CallToAction['id'] | undefined) => void;
  loadingFlyer: boolean;
  isExpired: boolean;
  isPending?: boolean;
  ctas?: CallToAction[];
  featuredLinkIds?: string[];
  onLoadFlyer: () => void;
  audienceId?: string | null;
  'aria-describedby'?: string;
  trackingFrom: TrackingFrom;
  distributionsReleased?: number;
};

type ExtendedCTA = CallToAction & {
  left: number;
  top: number;
  width: number;
  height: number;
};

export default function InteractiveFlyer({
  flyerId,
  districtId,
  pageNumber,
  flyerImage,
  highlightedLink,
  highlightLink,
  isExpired,
  ctas,
  featuredLinkIds,
  loadingFlyer,
  onLoadFlyer,
  audienceId,
  'aria-describedby': ariaDescribedBy,
  trackingFrom,
  isPending = false,
  distributionsReleased,
}: Props) {
  const { allLinks } = useGetFeaturedLinks(ctas, featuredLinkIds);
  const { trackMetrics } = useMetrics();
  const { trackEvent } = useAnalytics();
  const [widthRatio, setWidthRatio] = useState(0);
  const [heightRatio, setHeightRatio] = useState(0);
  const onFlyerImageLoad = (imageElement: HTMLImageElement) => {
    setWidthRatio(imageElement.clientWidth / imageElement.naturalWidth);
    setHeightRatio(imageElement.clientHeight / imageElement.naturalHeight);
    onLoadFlyer();
  };

  const links = useMemo(
    () =>
      allLinks
        .filter(link => link.paragraph && link.paragraph.pageNumber + 1 === pageNumber)
        .map<ExtendedCTA>(link => ({
          ...link,
          left: widthRatio * link.paragraph!.vertices[0].x,
          top: heightRatio * link.paragraph!.vertices[0].y,
          width: widthRatio * (link.paragraph!.vertices[1].x - link.paragraph!.vertices[0].x),
          height: heightRatio * (link.paragraph!.vertices[1].y - link.paragraph!.vertices[0].y),
        })),
    [allLinks, heightRatio, pageNumber, widthRatio]
  );

  return (
    <div className={cx(styles.container)}>
      <div className="swiper-zoom-container">
        <img
          id="overlayFlyer"
          data-testid="overlayFlyer"
          className={cx(
            styles.image,
            styles.overlay,
            isExpired && styles.expiredFlyerOverlay,
            isPending && styles.pendingFlyerOverlay
          )}
          src={flyerImage}
          alt={`Flyer image ${pageNumber}. Content available in following Accessible Text version`}
          onLoad={event => onFlyerImageLoad(event.target as HTMLImageElement)}
          aria-describedby={ariaDescribedBy}
        />
        <img
          data-testid="dimmedFlyer"
          className={cx(
            styles.image,
            links.length > 0 && styles.dimmed,
            isExpired && styles.expiredFlyerOverlay,
            isPending && styles.pendingFlyerOverlay
          )}
          src={flyerImage}
          alt=""
          role="presentation"
        />
      </div>
      {links.map(link => {
        const validHref = getValidHref(link.value, link.type);
        return (
          <a
            key={link.id}
            onClick={() => {
              const metrict = getCTAMetric(link.type) ?? '';
              const trackingData = getFeatureLinkTracking(link.type as CallToActionEnum);
              trackEvent({
                ...trackingData,
                ...trackingFrom,
              });
              trackMetrics({
                flyerId: flyerId.toString(),
                metric: metrict,
                action: 'pa-cta',
                resource: 'districts',
                resourceId: districtId.toString(),
                featureId: link.id,
                audienceId,
                distributionsReleased,
              });
            }}
            data-testid={`interactiveLink-${link.id}`}
            className={styles.link}
            href={getValidHref(link.value, link.type)}
            style={{
              left: `${link.left}px`,
              top: `${link.top}px`,
              width: `${link.width}px`,
              height: `${link.height}px`,
            }}
            onMouseEnter={() => highlightLink(link.id)}
            onMouseLeave={() => highlightLink(undefined)}
            rel="noopener noreferrer"
            target={link.type === 'email' || link.type === 'call' ? '_self' : '_blank'}
            tabIndex={-1}
            aria-label={`Link to ${link.type || 'resource'} ${validHref}`}
          >
            <span className="sr-only">
              Link to {link.type === 'url' ? 'a web resource' : link.type || 'resource'} {validHref}
            </span>
          </a>
        );
      })}
      <svg width="0" height="0" aria-hidden="true">
        <defs>
          <clipPath id="flyerClipPath">
            {links.map(
              link =>
                (highlightedLink === undefined || highlightedLink === link.id) && (
                  <rect
                    x={link.left}
                    y={link.top}
                    width={link.width}
                    height={link.height}
                    key={link.id}
                  />
                )
            )}
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}
